import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Select, Space, Table, Typography } from "antd";
import FlightDateDisplay from "../../../components/DateTimeDisplay/FlightDateDisplay";
import FlightTimeDisplay from "../../../components/DateTimeDisplay/FlightTimeDisplay";
import { UTCDateRangePicker } from "../../../components/DateTimeInput";
import { useTableSelection } from "../../../hooks/useTableSelection";
import { getDayOfWeekUTC } from "../../../utils/dateFormatterUtils";
import { calculateBlockHours, formatDuration } from "../../../utils/timeUtils";
import { convertFromUTC } from "../../../utils/timezoneUtils";
import { getDataRecordId, getDataRecordIds, getDataRecords } from "../utils/dataScopeResolver";

const { Text } = Typography;

const TABLE_SELECTION_SCOPE = '[data-table-selection-scope="content-block-data-scope"]';
const TABLE_SELECTION_ALLOW_SELECTORS = [TABLE_SELECTION_SCOPE];
const DATE_FILTER_FORMAT = "YYYY-MM-DD";
const DEPARTURE_DATE_FILTER_KEY = "departure_date";
const EMPTY_FILTER_DIMENSIONS = [];

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const normalizeSelectValue = (value) => String(value || "").trim().toUpperCase();

const isMultiSelectCriterion = (criterion) => criterion?.type === "multiSelect" || criterion?.comparator === "in";

const isDateCriterion = (criterion) => criterion?.type === "date" || criterion?.key === DEPARTURE_DATE_FILTER_KEY;

const isActiveCriterion = (criterion) => {
    if (!Array.isArray(criterion?.value)) return false;
    return isMultiSelectCriterion(criterion)
        ? criterion.value.some((value) => normalizeSelectValue(value))
        : criterion.value.length === 2;
};

const parseUtcValue = (value) => {
    if (!hasValue(value)) return null;

    const parsed = convertFromUTC(value, "UTC");
    return parsed?.isValid?.() ? parsed : null;
};

const getUtcDateString = (value) => {
    const parsed = parseUtcValue(value);
    return parsed ? parsed.format(DATE_FILTER_FORMAT) : null;
};

const normalizeFilterCriteria = (filterCriteria, filterDimensions = []) => {
    if (!filterCriteria || typeof filterCriteria !== "object") return {};
    const allowedKeys = new Set(filterDimensions.map((dimension) => dimension.key));

    return Object.entries(filterCriteria).reduce((criteria, [key, criterion]) => {
        if (allowedKeys.has(key)) {
            criteria[key] = criterion;
        }
        return criteria;
    }, {});
};

const hasActiveCriteria = (filterCriteria) => {
    return Object.values(filterCriteria || {}).some(isActiveCriterion);
};

const getRangePickerValue = (filterCriteria, dimension) => {
    const value = filterCriteria?.[dimension.key]?.value;
    if (!Array.isArray(value) || value.length !== 2) return undefined;

    const start = parseUtcValue(value[0]);
    const end = parseUtcValue(value[1]);
    if (!start || !end) return undefined;

    return [start, end];
};

const getMultiSelectValue = (filterCriteria, dimension) => {
    const value = filterCriteria?.[dimension.key]?.value;
    return Array.isArray(value) ? value.map(normalizeSelectValue).filter(Boolean) : [];
};

const getEffectiveDepartureDate = (record) => {
    const departureValue = record?.std;
    if (!hasValue(departureValue)) return getUtcDateString(record?.flight_date);

    const departureDate = getUtcDateString(departureValue);
    if (departureDate) return departureDate;

    return getUtcDateString(record?.flight_date);
};

const getRecordFilterValue = (record, criterion) => {
    if (criterion.key === DEPARTURE_DATE_FILTER_KEY || criterion.field === DEPARTURE_DATE_FILTER_KEY) {
        return getEffectiveDepartureDate(record);
    }

    return record?.[criterion.field];
};

const applyFilterCriteria = (records, filterCriteria) => {
    const activeCriteria = Object.values(filterCriteria || {}).filter(isActiveCriterion);
    if (activeCriteria.length === 0) return records;

    return records.filter((record) => {
        return activeCriteria.every((criterion) => {
            const recordValue = getRecordFilterValue(record, criterion);
            if (!recordValue) return false;

            if (isMultiSelectCriterion(criterion)) {
                const selectedValues = new Set(criterion.value.map(normalizeSelectValue).filter(Boolean));
                return selectedValues.has(normalizeSelectValue(recordValue));
            }

            const [startValue, endValue] = criterion.value;

            if (isDateCriterion(criterion)) {
                const recordDate = getUtcDateString(recordValue);
                const startDate = getUtcDateString(startValue);
                const endDate = getUtcDateString(endValue);

                if (!recordDate || !startDate || !endDate) return false;
                return recordDate >= startDate && recordDate <= endDate;
            }

            const recordTime = parseUtcValue(recordValue)?.valueOf();
            const startTime = parseUtcValue(startValue)?.valueOf();
            const endTime = parseUtcValue(endValue)?.valueOf();

            if (!Number.isFinite(recordTime) || !Number.isFinite(startTime) || !Number.isFinite(endTime)) return false;
            return recordTime >= startTime && recordTime <= endTime;
        });
    });
};

const getSelectOptions = (records, dimension) => {
    const values = new Set();

    records.forEach((record) => {
        const value = record?.[dimension.field];
        if (hasValue(value)) {
            const normalizedValue = normalizeSelectValue(value);
            if (normalizedValue) values.add(normalizedValue);
        }
    });

    return Array.from(values)
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value }));
};

const getBlockHoursDisplay = (record) => {
    return formatDuration(calculateBlockHours(record?.std, record?.sta), "HH:mm");
};

const getGroundTimeDisplay = (record) => {
    const groundTimeMinutes = record?.actual_ground_time_minutes ?? record?.ground_time_minutes;
    return groundTimeMinutes !== null && groundTimeMinutes !== undefined ? formatDuration(groundTimeMinutes, "h_m") : "-";
};

const DataScopeFilterModal = ({ open, instance, rawData, filterDimensions = EMPTY_FILTER_DIMENSIONS, onApply, onCancel }) => {
    const sourceRecords = useMemo(() => getDataRecords(rawData), [rawData]);
    const sourceIds = useMemo(() => getDataRecordIds(rawData), [rawData]);
    const sourceSignature = useMemo(() => sourceIds.map((id) => String(id)).join("|"), [sourceIds]);
    const selectOptionsByDimension = useMemo(
        () =>
            filterDimensions.reduce((optionsByKey, dimension) => {
                if (isMultiSelectCriterion(dimension)) {
                    optionsByKey[dimension.key] = getSelectOptions(sourceRecords, dimension);
                }
                return optionsByKey;
            }, {}),
        [filterDimensions, sourceRecords],
    );
    const instanceFilterState = instance?.filterState;
    const [filterCriteria, setFilterCriteria] = useState({});

    const filteredRecords = useMemo(() => applyFilterCriteria(sourceRecords, filterCriteria), [sourceRecords, filterCriteria]);
    const pagination = useMemo(() => ({ current: 1, pageSize: filteredRecords.length || 10 }), [filteredRecords.length]);
    const getRowKey = useCallback((record, index) => getDataRecordId(record) ?? `row-${index}`, []);

    const { selectedRowKeys, tableRef, rowSelection, getRowProps, handleRowSelectionChange, getIndexColumn, clearSelection } = useTableSelection({
        getRowKey,
        dataSource: filteredRecords,
        pagination,
        eventAllowSelectors: TABLE_SELECTION_ALLOW_SELECTORS,
    });

    const selectRecords = useCallback(
        (records) => {
            const keys = records.map((record, index) => getRowKey(record, index));
            handleRowSelectionChange(keys, records);
        },
        [getRowKey, handleRowSelectionChange],
    );

    useEffect(() => {
        if (!open) return;

        const nextCriteria = normalizeFilterCriteria(instanceFilterState?.filterCriteria, filterDimensions);
        const initialFilteredRecords = applyFilterCriteria(sourceRecords, nextCriteria);
        const selectedIdSet = Array.isArray(instanceFilterState?.selectedIds) ? new Set(instanceFilterState.selectedIds.map((id) => String(id))) : null;
        const initialSelectedRecords = selectedIdSet ? initialFilteredRecords.filter((record) => selectedIdSet.has(String(getDataRecordId(record)))) : initialFilteredRecords;

        setFilterCriteria(nextCriteria);
        selectRecords(initialSelectedRecords);
    }, [open, instance?.instanceId, instanceFilterState?.updatedAt, instanceFilterState?.selectedIds, instanceFilterState?.filterCriteria, filterDimensions, sourceRecords, sourceSignature, selectRecords]);

    const handleRangeChange = useCallback(
        (dimension, range) => {
            const nextCriteria = { ...filterCriteria };

            if (Array.isArray(range) && range[0] && range[1]) {
                nextCriteria[dimension.key] = {
                    key: dimension.key,
                    label: dimension.label,
                    field: dimension.field,
                    type: dimension.type,
                    comparator: dimension.comparator,
                    value: dimension.type === "date" ? [range[0].format(DATE_FILTER_FORMAT), range[1].format(DATE_FILTER_FORMAT)] : [range[0].toISOString(), range[1].toISOString()],
                };
            } else {
                delete nextCriteria[dimension.key];
            }

            const nextFilteredRecords = applyFilterCriteria(sourceRecords, nextCriteria);
            setFilterCriteria(nextCriteria);
            selectRecords(nextFilteredRecords);
        },
        [filterCriteria, selectRecords, sourceRecords],
    );

    const handleMultiSelectChange = useCallback(
        (dimension, values) => {
            const nextCriteria = { ...filterCriteria };
            const selectedValues = Array.isArray(values) ? values.map(normalizeSelectValue).filter(Boolean) : [];

            if (selectedValues.length > 0) {
                nextCriteria[dimension.key] = {
                    key: dimension.key,
                    label: dimension.label,
                    field: dimension.field,
                    type: dimension.type,
                    comparator: dimension.comparator,
                    value: selectedValues,
                };
            } else {
                delete nextCriteria[dimension.key];
            }

            const nextFilteredRecords = applyFilterCriteria(sourceRecords, nextCriteria);
            setFilterCriteria(nextCriteria);
            selectRecords(nextFilteredRecords);
        },
        [filterCriteria, selectRecords, sourceRecords],
    );

    const handleSelectFiltered = useCallback(() => {
        selectRecords(filteredRecords);
    }, [filteredRecords, selectRecords]);

    const handleApply = useCallback(() => {
        const selectedKeySet = new Set(selectedRowKeys.map((key) => String(key)));
        const selectedIds = sourceRecords
            .filter((record) => selectedKeySet.has(String(getDataRecordId(record))))
            .map(getDataRecordId)
            .filter((id) => id !== null && id !== undefined);

        const selectedAllSource = sourceIds.length === selectedIds.length && sourceIds.every((id) => selectedKeySet.has(String(id)));
        const nextFilterState = {
            selectedIds: !hasActiveCriteria(filterCriteria) && selectedAllSource ? null : selectedIds,
            sourceIds,
            filterCriteria,
            updatedAt: new Date().toISOString(),
        };

        onApply?.(nextFilterState);
    }, [filterCriteria, onApply, selectedRowKeys, sourceIds, sourceRecords]);

    const handleReset = useCallback(() => {
        onApply?.({
            selectedIds: null,
            sourceIds,
            filterCriteria: {},
            updatedAt: new Date().toISOString(),
        });
    }, [onApply, sourceIds]);

    const renderFilterControl = useCallback(
        (dimension) => {
            if (isMultiSelectCriterion(dimension)) {
                return (
                    <Select
                        allowClear
                        mode="multiple"
                        maxTagCount="responsive"
                        options={selectOptionsByDimension[dimension.key] || []}
                        placeholder={`选择${dimension.label}`}
                        value={getMultiSelectValue(filterCriteria, dimension)}
                        onChange={(values) => handleMultiSelectChange(dimension, values)}
                        style={{ minWidth: 220 }}
                    />
                );
            }

            return (
                <UTCDateRangePicker
                    allowClear
                    format={DATE_FILTER_FORMAT}
                    placeholder={["开始日期", "结束日期"]}
                    value={getRangePickerValue(filterCriteria, dimension)}
                    onChange={(range) => handleRangeChange(dimension, range)}
                    style={{ minWidth: 260 }}
                />
            );
        },
        [filterCriteria, handleMultiSelectChange, handleRangeChange, selectOptionsByDimension],
    );

    const columns = useMemo(
        () => [
            getIndexColumn("序号", 64),
            {
                title: "Date",
                key: "date",
                width: 96,
                render: (_, record) => (
                    <FlightDateDisplay
                        etdOrStd={record.std}
                        departureAirportCode={record.departure_airport}
                        originalFlightDateForFallback={record.flight_date}
                        targetFormat="DD-MMM"
                    />
                ),
            },
            {
                title: "A/C REG",
                dataIndex: "aircraft_reg",
                key: "aircraft_reg",
                width: 100,
                render: (value) => value || "-",
            },
            {
                title: "Day",
                key: "day",
                width: 70,
                render: (_, record) => getDayOfWeekUTC(record.std || record.flight_date) || "-",
            },
            {
                title: "Flight",
                dataIndex: "flight_number",
                key: "flight_number",
                width: 100,
                render: (value) => value || "-",
            },
            {
                title: "DEP",
                dataIndex: "departure_airport",
                key: "departure_airport",
                width: 80,
                render: (value) => value || "-",
            },
            {
                title: "ARR",
                dataIndex: "arrival_airport",
                key: "arrival_airport",
                width: 80,
                render: (value) => value || "-",
            },
            {
                title: "STD",
                key: "departure_time",
                width: 120,
                render: (_, record) => (record.std ? <FlightTimeDisplay utcTime={record.std} airportCode={record.departure_airport} format="HH:mm" /> : "-"),
            },
            {
                title: "STA",
                key: "arrival_time",
                width: 120,
                render: (_, record) => (
                    record.sta ? (
                        <FlightTimeDisplay
                            utcTime={record.sta}
                            airportCode={record.arrival_airport}
                            baseUtcTime={record.std}
                            stdTime={record.std}
                            format="HH:mm"
                        />
                    ) : "-"
                ),
            },
            {
                title: "STC",
                dataIndex: "stc_type",
                key: "stc_type",
                width: 80,
                render: (value) => value || "-",
            },
            {
                title: "BH",
                key: "block_hours",
                width: 80,
                render: (_, record) => getBlockHoursDisplay(record),
            },
            {
                title: "GT",
                key: "ground_time",
                width: 80,
                render: (_, record) => getGroundTimeDisplay(record),
            },
        ],
        [getIndexColumn],
    );

    return (
        <Modal
            title="筛选航班"
            open={open}
            onCancel={onCancel}
            onOk={handleApply}
            className="xl-modal"
            okText="应用筛选"
            cancelText="取消"
            destroyOnHidden
            footer={[
                <Button key="reset" onClick={handleReset}>
                    恢复全量
                </Button>,
                <Button key="cancel" onClick={onCancel}>
                    取消
                </Button>,
                <Button key="submit" type="primary" onClick={handleApply}>
                    应用筛选
                </Button>,
            ]}
        >
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <div style={{ padding: 12, border: "1px solid #f0f0f0", borderRadius: 6, background: "#fafafa" }}>
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                        <Space wrap>
                            {filterDimensions.map((dimension) => (
                                <Space key={dimension.key} direction="vertical" size={4}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{dimension.label}</Text>
                                    {renderFilterControl(dimension)}
                                </Space>
                            ))}
                        </Space>
                    </Space>
                </div>

                <div>
                    <Space style={{ marginBottom: 8, width: "100%", justifyContent: "space-between" }}>
                        <Text>
                            当前显示 <Text strong>{filteredRecords.length}</Text> / {sourceRecords.length} 条，已选择 <Text strong>{selectedRowKeys.length}</Text> 条
                        </Text>
                        <Space>
                            <Button size="small" onClick={handleSelectFiltered}>全选当前筛选结果</Button>
                            <Button size="small" onClick={clearSelection}>清空选择</Button>
                        </Space>
                    </Space>
                    <div data-table-selection-scope="content-block-data-scope">
                        <Table
                            size="small"
                            rowKey={getRowKey}
                            ref={tableRef}
                            columns={columns}
                            dataSource={filteredRecords}
                            rowSelection={{ ...rowSelection, columnWidth: 6 }}
                            onRow={getRowProps}
                            pagination={false}
                            scroll={{ x: 1120, y: 560 }}
                        />
                    </div>
                </div>
            </Space>
        </Modal>
    );
};

export default DataScopeFilterModal;
